import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Bell, X, Check, CheckCheck, Trash2, AlertTriangle, Info, Calendar,
  DollarSign, FileWarning, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  warning: { icon: AlertTriangle, color: 'text-amber-500' },
  info: { icon: Info, color: 'text-blue-500' },
  success: { icon: Check, color: 'text-green-500' },
  error: { icon: AlertTriangle, color: 'text-red-500' },
};

const REF_ICONS = {
  document_expiry: FileWarning,
  payment_reminder: DollarSign,
  upcoming_game: Calendar,
};

export default function NotificationBell() {
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications/`);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`);
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`);
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`);
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const generateAlerts = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/notifications/generate-alerts`);
      fetchNotifications();
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        className={`relative p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-200'}`}
        data-testid="notification-bell"
      >
        <Bell className={`h-5 w-5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-xl shadow-2xl border z-50 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="notification-panel">
          {/* Header */}
          <div className={`flex items-center justify-between p-3 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>Notifications</h3>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={generateAlerts} disabled={generating} className="h-7 text-xs" data-testid="generate-alerts-btn">
                {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
              </Button>
              {unreadCount > 0 && (
                <Button size="sm" variant="ghost" onClick={markAllRead} className="h-7 text-xs" data-testid="mark-all-read-btn">
                  <CheckCheck className="h-3 w-3 mr-1" />All
                </Button>
              )}
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className={`p-6 text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
                <p className="text-xs mt-1">Click Refresh to check for alerts</p>
              </div>
            ) : (
              notifications.map((n) => {
                const typeCfg = TYPE_ICONS[n.type] || TYPE_ICONS.info;
                const TypeIcon = typeCfg.icon;
                const RefIcon = REF_ICONS[n.ref_type] || Info;

                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 p-3 border-b transition-colors ${
                      n.read
                        ? isDark ? 'border-slate-700/50' : 'border-slate-100'
                        : isDark ? 'bg-slate-700/30 border-slate-700' : 'bg-blue-50/50 border-slate-200'
                    }`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`mt-0.5 p-1.5 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                      <RefIcon className={`h-4 w-4 ${typeCfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'} ${n.read ? 'opacity-60' : ''}`}>
                        {n.title}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'} ${n.read ? 'opacity-60' : ''}`}>
                        {n.message}
                      </p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {new Date(n.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!n.read && (
                        <button onClick={() => markRead(n.id)} className={`p-1 rounded ${isDark ? 'hover:bg-slate-600' : 'hover:bg-slate-200'}`} title="Mark read">
                          <Check className="h-3 w-3 text-green-500" />
                        </button>
                      )}
                      <button onClick={() => deleteNotification(n.id)} className={`p-1 rounded ${isDark ? 'hover:bg-slate-600' : 'hover:bg-slate-200'}`} title="Delete">
                        <Trash2 className="h-3 w-3 text-red-400" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
